import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { AppLayout } from './AppLayout';
import { GameScreen } from './screens/GameScreen';
import { HomeScreen } from './screens/HomeScreen';
import { LiveScreen } from './screens/LiveScreen';
import { ProfileScreen } from './screens/ProfileScreen';

// Module 33 (WP-BG.1) — the Background Studio is a DEV-ONLY authoring tool. It is
// lazy-imported and only added to the route table under import.meta.env.DEV, so
// neither the screen nor its CSS are referenced by (and thus tree-shaken from) a
// production build. A static `/dev/backgrounds` out-ranks AppLayout's `*`
// catch-all, so it resolves before the redirect-to-home.
const devRoutes: RouteObject[] = [];
if (import.meta.env.DEV) {
  const BackgroundStudioScreen = lazy(() => import('./dev/backgrounds/BackgroundStudioScreen'));
  devRoutes.push({
    path: '/dev/backgrounds',
    element: (
      <Suspense fallback={null}>
        <BackgroundStudioScreen />
      </Suspense>
    ),
  });
  // Module 35 (WP-35.2) — Block Jam Level Studio, same DEV-ONLY gating: lazy +
  // guarded by import.meta.env.DEV so neither the screen nor its CSS ship in a
  // production build. Static path out-ranks AppLayout's `*` catch-all.
  const BlockJamStudioScreen = lazy(() => import('./dev/blockjam/BlockJamStudioScreen'));
  devRoutes.push({
    path: '/dev/blockjam',
    element: (
      <Suspense fallback={null}>
        <BlockJamStudioScreen />
      </Suspense>
    ),
  });
  // Module 39 (WP-B.6) — the Audio Studio, same DEV-ONLY gating. It grew out of
  // Module 37's audition page (WP-37.7) and still does that job on its Library
  // tab: every sound in the app, both games, through the real AudioService. New
  // is the Assign tab, which changes which sound a cue uses without editing a
  // script. Persistence is the dev-only /__audio middleware.
  const AudioStudioScreen = lazy(() => import('./dev/audio/AudioStudioScreen'));
  devRoutes.push({
    path: '/dev/audio',
    element: (
      <Suspense fallback={null}>
        <AudioStudioScreen />
      </Suspense>
    ),
  });
}

export const router = createBrowserRouter([
  ...devRoutes,
  // Game routes live OUTSIDE AppLayout by design (Module 28): the Lobby chrome
  // (GlobalHeader/XPBar/NavigationBar) must be structurally incapable of rendering
  // while a game is active — not merely hidden by a pathname check.
  { path: '/game/:id', element: <GameScreen /> },
  {
    element: <AppLayout />,
    children: [
      { path: '/', element: <HomeScreen /> },
      { path: '/live', element: <LiveScreen /> },
      { path: '/profile', element: <ProfileScreen /> },
      // /shop + /settings routes retired (WP-NAV.6/WP-NAV.9) — both are shared
      // popups now (app-level ShopPopupContainer; AppLayout's SettingsPopup via
      // the header's top-right gear). /legal/* retired too (WP-NAV.4) — static
      // pages opened externally. _redirects keeps all old URLs alive.
      // Catch-all (WP-NAV.6): any unknown/retired path lands on Home instead of
      // the router's raw error page — covers stale client-side navigations to
      // retired routes (e.g. a not-yet-migrated pushState to /shop) gracefully.
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
